import { useImperativeHandle, forwardRef, useEffect, useState, useRef } from "react";
import {
    mortgageTypeText,
    mortgageAmountText,
    mortgageTermText,
    interestRateText,
    interestFrequencyText,
    repaymentFrequnecyText,
    offsetAmountText,
    housePriceAppreciationText,
    overpaymentsText,
    depositText,
    offsetRegularText,
    productFeeText,
    boeRateText,
    nominalText,
     dateText
} from "../constants/TooltipConstants";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faKeyboard, faGears, faPiggyBank, faHouseUser, faMoneyBillTrendUp, faBuildingColumns, faQuestion } from '@fortawesome/free-solid-svg-icons'
import SaveInputs, { SaveInputsRef } from "./SaveInputs";
import { useAuth } from "../contexts/AuthProvider";

export interface MortgageData {
    mortgageType: string,
    interestFrequency: string,
    interestValue: string,
    mortgageAmountValue: string,
    mortgageTermValue: string,
    repaymentFrequency: string,
    showOverpayments: boolean,
    overpaymentsValue: string,
    depositValue: string,
    showOffset: boolean,
    offsetValue: string,
    showEquity: boolean,
    housePriceInterestValue: string,
    showAdvanced: boolean,
    offsetRegular: string,
    addBOE: boolean,
    productFee: string,
    useNominal: boolean,
    date: string
};

interface InputFormProps {
    updateData: (data: MortgageData) => void;
    isCompareVisible: boolean;
    whichMortgage: string;
}

export type InputFormRef = { // For clearing inputs
    resetForm: () => void;
};


const InputForm = forwardRef<InputFormRef, InputFormProps>(({ updateData, isCompareVisible, whichMortgage }, ref) => {
    const saveInputRef = useRef<SaveInputsRef>(null);
    const defaultValues = {
        showAdvanced: false,
        showAdvancedCollapse: "collapse-close",
        showOffset: false,
        showOffsetCollapse: "collapse-close",
        showOverpayments: false,
        showOverpaymentsCollapse: "collapse-close",
        showDeposits: false,
        showEquity: false,
        showHousePriceCollapse: "collapse-close",
        mortgageType: 'repayment',
        interestFrequency: '',
        interestValue: '',
        repaymentFrequency: '',
        mortgageTermValue: '',
        mortgageAmountValue: '',
        offsetValue: '',
        feesValue: '',
        overpaymentsValue: '',
        depositValue: '',
        housePriceInterestValue: '',
        offsetRegular: '',
        addBOE: false,
        useNominal: false,
        date: ''
    };
    const defaultData: MortgageData = {
        mortgageType: defaultValues.mortgageType,
        interestFrequency: defaultValues.interestFrequency,
        interestValue: '0',
        mortgageAmountValue: '0',
        mortgageTermValue: '0',
        repaymentFrequency: defaultValues.repaymentFrequency,
        showOverpayments: defaultValues.showOverpayments,
        overpaymentsValue: '0',
        depositValue: '0',
        showOffset: defaultValues.showOffset,
        offsetValue: '0',
        showEquity: defaultValues.showEquity,
        housePriceInterestValue: '0',
        showAdvanced: defaultValues.showAdvanced,
        offsetRegular: '0',
        addBOE: defaultValues.addBOE,
        productFee: '0',
        useNominal: defaultValues.useNominal,
        date: defaultValues.date
    }

    useImperativeHandle(ref, () => ({ // When inputs need to be cleared
        resetForm: () => {
            setShowAdvanced(defaultValues.showAdvanced);
            setShowAdvancedCollapse(defaultValues.showAdvancedCollapse);
            setShowOffset(defaultValues.showOffset);
            setShowOffsetCollapse(defaultValues.showOffsetCollapse);
            setShowOverpayments(defaultValues.showOverpayments);
            setShowOverpaymentsCollapse(defaultValues.showOverpaymentsCollapse);
            setShowDeposits(defaultValues.showDeposits);
            setShowEquity(defaultValues.showEquity);
            setShowHousePriceCollapse(defaultValues.showHousePriceCollapse);
            setMortgageType(defaultValues.mortgageType);
            setInterestFrequency(defaultValues.interestFrequency);
            setInterestValue(defaultValues.interestValue);
            setRepaymentFrequency(defaultValues.repaymentFrequency);
            setMortgageTermValue(defaultValues.mortgageTermValue);
            setMortgageAmountValue(defaultValues.mortgageAmountValue);
            setOffsetValue(defaultValues.offsetValue);
            setOverpaymentsValue(defaultValues.overpaymentsValue);
            setDepositValue(defaultValues.depositValue);
            setProductFeeValue(defaultValues.feesValue);
            setHousePriceInterestValue(defaultValues.housePriceInterestValue);
            setAddBOE(defaultValues.addBOE);
            setUseNominal(defaultValues.useNominal);
            setDateValue(defaultValues.date);
            saveInputRef.current?.resetForm();
        },
    }));

    const mortgageTypeOptions = [
        {
            label: "Repayment",
            value: "repayment",
        },
        {
            label: "Interest-Only",
            value: "interestOnly",
        }
    ];

    const repaymentFrequencyOptions = [
        {
            label: "Monthly",
            value: "monthly",
        },
        {
            label: "Accelerated Bi-Weekly",
            value: "accelerated biweekly",
        },
        {
            label: "Bi-Weekly",
            value: "fortnightly",
        },
        {
            label: "Accelerated Weekly",
            value: "accelerated weekly",
        },
        {
            label: "Weekly",
            value: "weekly",
        }
    ];

    const interestFrequencyOptions = [
        {
            label: "Monthly",
            value: "monthly",
        },
        {
            label: "Daily",
            value: "daily",
        }
    ];

    const [showAdvanced, setShowAdvanced] = useState<boolean>(defaultValues.showAdvanced);
    const [showAdvancedCollapse, setShowAdvancedCollapse] = useState<string>(defaultValues.showAdvancedCollapse);
    const [showOffset, setShowOffset] = useState<boolean>(defaultValues.showOffset);
    const [showOffsetCollapse, setShowOffsetCollapse] = useState<string>(defaultValues.showOffsetCollapse);
    const [showOverpayments, setShowOverpayments] = useState<boolean>(defaultValues.showOverpayments);
    const [showOverpaymentsCollapse, setShowOverpaymentsCollapse] = useState<string>(defaultValues.showOverpaymentsCollapse);
    const [showDeposits, setShowDeposits] = useState<boolean>(defaultValues.showDeposits);
    const [showEquity, setShowEquity] = useState<boolean>(defaultValues.showEquity);
    const [showHousePriceCollapse, setShowHousePriceCollapse] = useState<string>(defaultValues.showHousePriceCollapse);

    // Toggles for the hidden cards
    const handleAdvancedToggleChange = () => {
        if (showAdvanced) {
            setShowAdvancedCollapse("collapse-close");
        } else {
            setShowAdvancedCollapse("collapse-open");
        }
        setShowAdvanced(!showAdvanced);
    };

    const handleOffsetToggleChange = () => {
        if (showOffset) {
            setShowOffsetCollapse("collapse-close");
        } else {
            setShowOffsetCollapse("collapse-open");
        }
        setShowOffset(!showOffset);
    };

    const handleOverpaymentsToggleChange = () => {
        if (showOverpayments) {
            setShowOverpaymentsCollapse("collapse-close");
        } else {
            setShowOverpaymentsCollapse("collapse-open");
        }
        setShowOverpayments(!showOverpayments);
    };

    const handleHousePriceToggleChange = () => {
        if (showEquity) {
            setShowHousePriceCollapse("collapse-close");
        } else {
            setShowHousePriceCollapse("collapse-open");
        }
        setShowEquity(!showEquity);
    };

    // Input variables
    const [mortgageType, setMortgageType] = useState<string>(defaultValues.mortgageType);
    const [interestFrequency, setInterestFrequency] = useState<string>(defaultValues.interestFrequency);
    const [interestValue, setInterestValue] = useState<string>(defaultValues.interestValue);
    const [repaymentFrequency, setRepaymentFrequency] = useState<string>(defaultValues.repaymentFrequency);
    const [mortgageTermValue, setMortgageTermValue] = useState<string>(defaultValues.mortgageTermValue);
    const [mortgageAmountValue, setMortgageAmountValue] = useState<string>(defaultValues.mortgageAmountValue);
    const [offsetValue, setOffsetValue] = useState<string>(defaultValues.offsetValue);
    const [offsetRegularValue, setOffsetRegularValue] = useState<string>(defaultValues.offsetRegular);
    const [overpaymentsValue, setOverpaymentsValue] = useState<string>(defaultValues.overpaymentsValue);
    const [depositValue, setDepositValue] = useState<string>(defaultValues.depositValue);
    const [productFeeValue, setProductFeeValue] = useState<string>(defaultValues.feesValue);
    const [housePriceInterestValue, setHousePriceInterestValue] = useState<string>(defaultValues.housePriceInterestValue);
    const [addBOE, setAddBOE] = useState<boolean>(defaultValues.addBOE);
    const [useNominal, setUseNominal] = useState<boolean>(defaultValues.useNominal);
    const [dateValue, setDateValue] = useState<string>(defaultValues.date);

    const [mounted, setMounted] = useState<boolean>(false);
    const [loadedSave, setLoadedSave] = useState<boolean>(false);
    useEffect(() => {
        
        if (!mounted) { // Ensures chart and table stay empty on first render
            setMounted(true);
            return;
        }
        if (mortgageType === "interestOnly") {
            setInterestFrequency("monthly");
            setShowOffset(false);
            setShowOffsetCollapse("collapse-close");
            setShowOverpayments(false);
            setShowOverpaymentsCollapse("collapse-close");
        }
        console.log(loadedSave)
        if (!loadedSave) {
            // setLoadedSave(0)
            saveInputRef.current?.resetForm();
        } else {
            setLoadedSave(false)
        }
        
        if (mortgageTermValue !== '' && mortgageAmountValue !== '') {
            // 
            const data: MortgageData = {
                mortgageType: mortgageType,
                interestFrequency: (interestFrequency === '' ? 'monthly' : interestFrequency),
                interestValue: (interestValue === '' ? '0' : interestValue),
                mortgageAmountValue: (mortgageAmountValue === '' ? '0' : mortgageAmountValue),
                mortgageTermValue: (mortgageTermValue === '' ? '0' : mortgageTermValue),
                repaymentFrequency: (repaymentFrequency === '' ? 'monthly' : repaymentFrequency),
                showOverpayments: showOverpayments,
                overpaymentsValue: (overpaymentsValue === '' ? '0' : overpaymentsValue),
                depositValue: (depositValue === '' ? '0' : depositValue),
                showOffset: showOffset,
                offsetValue: (offsetValue === '' ? '0' : offsetValue),
                showEquity: showEquity,
                housePriceInterestValue: (housePriceInterestValue === '' ? '0' : housePriceInterestValue),
                showAdvanced: showAdvanced,
                offsetRegular: (offsetRegularValue === '' ? '0' : offsetRegularValue),
                addBOE: addBOE,
                productFee: (productFeeValue === '' ? '0' : productFeeValue),
                useNominal: useNominal,
                date: dateValue
            }
            updateData(data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mortgageType, interestFrequency, interestValue, mortgageTermValue, mortgageAmountValue,
        repaymentFrequency,
        showOverpayments, overpaymentsValue,
        showDeposits, depositValue,
        showEquity, housePriceInterestValue,
        showOffset, offsetValue, showAdvanced, offsetRegularValue,
        addBOE, productFeeValue, isCompareVisible, useNominal, dateValue]);

    const handleMortgageTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setMortgageType(event.target.value);
    };

    const handleRepaymentFrequencyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setRepaymentFrequency(event.target.value);
    };

    const handleInterestFrequencyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setInterestFrequency(event.target.value);
    };

    const handleInterestInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        // Allow the user to clear the input
        if (value === '') {
            setInterestValue(value);
        } else {
            const numValue = parseFloat(value);
            // Check if the value is positive
            if (numValue >= 0) {
                setInterestValue(value);
            }
        }
    };

    const handleMortgageTermInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value, 10);
        // If the value is a number and within the 1-40 range, update the state
        if (isNaN(value)) {
            setMortgageTermValue('');
        }
        else if (!isNaN(value) && value >= 1 && value <= 40) {
            setMortgageTermValue(event.target.value);
        } else if (value < 1) {
            // If the value is less than 1, set it to the minimum value
            setMortgageTermValue('1');
        } else if (value > 40) {
            // If the value is greater than 40, set it to the maximum value
            setMortgageTermValue('40');
        }
    };

    const handleMortgageAmountInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value === '') {
            setMortgageAmountValue(value);
        } else {
            const numValue = parseFloat(value);
            if (numValue >= 0) {
                setMortgageAmountValue(value);
            }
        }
    };

    const handleOffsetInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value === '') {
            setOffsetValue(value);
        } else {
            const numValue = parseFloat(value);
            if (numValue >= 0) {
                setOffsetValue(value);
            }
        }
    };

    const handleOffsetRegularInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOffsetRegularValue(event.target.value);
    };

    const handleOverpaymentsInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value === '') {
            setOverpaymentsValue(value);
        } else {
            const numValue = parseFloat(value);
            if (numValue >= 0) {
                setOverpaymentsValue(value);
            }
        }
    };

    const handleDepositInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value === '') {
            setDepositValue(value);
        } else {
            const numValue = parseFloat(value);
            if (numValue >= 0) {
                setDepositValue(value);
            }
        }
    };

    const handleProductFeeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value === '') {
            setProductFeeValue(value);
        } else {
            const numValue = parseFloat(value);
            if (numValue >= 0) {
                setProductFeeValue(value);
            }
        }
    };

    const handleDateInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDateValue(event.target.value);
    };

    const handleHousePriceInterestInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setHousePriceInterestValue(event.target.value);
    };

    const handleAddBOEToggleChange = () => {
        setAddBOE(!addBOE);
    };

    const handleNominalToggleChange = () => {
        setUseNominal(!useNominal);
    };

    const handleMortgageSelect = (id: string, data: MortgageData) => {
        setLoadedSave(true)
        setShowAdvanced(data.showAdvanced);
        setShowAdvancedCollapse(data.showAdvanced ? "collapse-open" : "collapse-close");
        setShowOffset(data.showOffset);
        setShowOffsetCollapse(data.showOffset ? "collapse-open" : "collapse-close");
        setShowOverpayments(data.showOverpayments);
        setShowOverpaymentsCollapse(data.showOverpayments ? "collapse-open" : "collapse-close");
        setShowEquity(data.showEquity);
        setShowHousePriceCollapse(data.showEquity ? "collapse-open" : "collapse-close");
        setMortgageType(data.mortgageType);
        setInterestFrequency(data.interestFrequency);
        setInterestValue(data.interestValue);
        setRepaymentFrequency(data.repaymentFrequency);
        setMortgageTermValue(data.mortgageTermValue);
        setMortgageAmountValue(data.mortgageAmountValue);
        setOffsetValue(data.offsetValue);
        setOverpaymentsValue(data.overpaymentsValue);
        setDepositValue(data.depositValue);
        setProductFeeValue(data.productFee);
        setHousePriceInterestValue(data.housePriceInterestValue);
        setAddBOE(data.addBOE);
        setUseNominal(data.useNominal);
        if (data.mortgageTermValue !== '' && data.mortgageAmountValue !== '') {
            const mortgageData: MortgageData = {
                mortgageType: mortgageType,
                interestFrequency: (interestFrequency === '' ? 'monthly' : interestFrequency),
                interestValue: (interestValue === '' ? '0' : interestValue),
                mortgageAmountValue: (mortgageAmountValue === '' ? '0' : mortgageAmountValue),
                mortgageTermValue: (mortgageTermValue === '' ? '0' : mortgageTermValue),
                repaymentFrequency: (repaymentFrequency === '' ? 'monthly' : repaymentFrequency),
                showOverpayments: showOverpayments,
                overpaymentsValue: (overpaymentsValue === '' ? '0' : overpaymentsValue),
                depositValue: (depositValue === '' ? '0' : depositValue),
                showOffset: showOffset,
                offsetValue: (offsetValue === '' ? '0' : offsetValue),
                showEquity: showEquity,
                housePriceInterestValue: (housePriceInterestValue === '' ? '0' : housePriceInterestValue),
                showAdvanced: showAdvanced,
                offsetRegular: (offsetRegularValue === '' ? '0' : offsetRegularValue),
                addBOE: addBOE,
                productFee: (productFeeValue === '' ? '0' : productFeeValue),
                useNominal: useNominal,
                date: dateValue
            }
            updateData(mortgageData);
        } else {
            updateData(defaultData);
        }
    };

    const backgroundColour = "bg-base-300"; // colour scheme
    const inputElement = "bg-primary";
    const spaceFromTooltip = "space-x-1"
    const { auth }: any = useAuth();

    return (
        <div className="space-y-4 flex-grow" >
            {auth ?
                (<div className={"grid card rounded-box " + backgroundColour}>
                    <div className="card-body p-3">
                        <SaveInputs mortgageData={{
                            mortgageType: mortgageType,
                            interestFrequency: interestFrequency,
                            interestValue: interestValue,
                            mortgageAmountValue: mortgageAmountValue,
                            mortgageTermValue: mortgageTermValue,
                            repaymentFrequency: repaymentFrequency,
                            showOverpayments: showOverpayments,
                            overpaymentsValue: overpaymentsValue,
                            depositValue: depositValue,
                            showOffset: showOffset,
                            offsetValue: offsetValue,
                            showEquity: showEquity,
                            housePriceInterestValue: housePriceInterestValue,
                            showAdvanced: showAdvanced,
                            offsetRegular: offsetRegularValue,
                            addBOE: addBOE,
                            productFee: productFeeValue,
                            useNominal: useNominal,
                            date: dateValue
                        }}
                            ref={saveInputRef}
                            whichMortgage={whichMortgage}
                            isCompareVisible={isCompareVisible}
                            onSelectMortgage={handleMortgageSelect} />
                    </div>
                </div>) : (<></>)}
            {/*-------------------------- Mortgage --------------------------------------*/}
            <div className={"grid card rounded-box " + backgroundColour}>
                <div className="card-body p-3">
                    <h2 className="card-title font-bold"><FontAwesomeIcon icon={faKeyboard} className="ml-2 mr-1" />{!isCompareVisible ? "Mortgage" : whichMortgage}</h2>
                    {/* mortgage type input */}
                    <div className={"flex items-center " + spaceFromTooltip}>
                        <select value={mortgageType} onChange={handleMortgageTypeChange} className="select select-bordered w-full">
                            <option disabled selected value={""}>Select your repayment type</option>
                            {mortgageTypeOptions.map((option) => (
                                <option value={option.value}>{option.label}</option>
                            ))}
                        </select>
                        <div className="tooltip tooltip-top tooltip-primary font-bold" data-tip={mortgageTypeText}>
                            <button className="btn btn-primary font-bold btn-circle"><FontAwesomeIcon icon={faQuestion} /></button>
                        </div>
                    </div>
                    {/* mortgage amount input */}
                    <div className={"flex items-center " + spaceFromTooltip}>
                        <label className="relative input-group">
                            <span className={inputElement + ' font-medium'}>£</span>
                            <span style={{ left: '3.25rem' }} className={`bg-transparent text-l font-normal text-gray-400 absolute px-2 transition-all ease-in-out duration-200 z-10 ${mortgageAmountValue ? 'transform -translate-y-1 -translate-x-3 scale-75' : 'translate-y-1/2'}`}>
                                Amount *
                            </span>
                            <input type="number" onChange={handleMortgageAmountInputChange} value={mortgageAmountValue} className="input input-bordered w-full" />
                        </label>

                        <div className="tooltip tooltip-top tooltip-primary font-bold" data-tip={mortgageAmountText}>
                            <button className="btn btn-primary font-bold btn-circle"><FontAwesomeIcon icon={faQuestion} /></button>
                        </div>
                    </div>
                    {/* mortgage term input */}
                    <div className={"flex items-center " + spaceFromTooltip}>
                        <label className="relative input-group">
                            <input type="number" onChange={handleMortgageTermInputChange} value={mortgageTermValue} className="input input-bordered w-full pl-4" />
                            <span className={`bg-transparent text-l font-normal text-gray-400 absolute left-3 px-2 transition-all ease-in-out duration-200 ${mortgageTermValue ? 'transform -translate-y-1 -translate-x-3 scale-75' : 'translate-y-1/2'}`}>
                                Term 1-40 *
                            </span>
                            <span className={inputElement + " font-medium"}>Years</span>
                        </label>
                        <div className="tooltip tooltip-top tooltip-primary font-bold" data-tip={mortgageTermText}>
                            <button className="btn btn-primary font-bold btn-circle"><FontAwesomeIcon icon={faQuestion} /></button>
                        </div>
                    </div>
                    {/* interest rate input */}
                    <div className={"flex items-center " + spaceFromTooltip}>
                        <label className="relative input-group">
                            <span className={inputElement + ' font-medium'}>%</span>
                            <span style={{ left: '3.5rem' }} className={`bg-transparent text-l font-normal text-gray-400 absolute px-2 transition-all ease-in-out duration-200 z-10 ${interestValue ? 'transform -translate-y-1 -translate-x-3 scale-75' : 'translate-y-1/2'}`}>
                                Interest *
                            </span>
                            <input type="number" onChange={handleInterestInputChange} value={interestValue} className="input input-bordered w-full" />
                        </label>
                        <div className="tooltip tooltip-top tooltip-primary font-bold" data-tip={interestRateText}>
                            <button className="btn btn-primary font-bold btn-circle"><FontAwesomeIcon icon={faQuestion} /></button>
                        </div>
                    </div>
                    {/* Add boe input */}
                    <div className={"flex items-center " + spaceFromTooltip}>
                        <kbd className="kbd w-full">
                            <label className="cursor-pointer label w-6/12 justify-center flex items-center">
                                <span className="label-text font-sans font-semibold whitespace-nowrap pr-2 "><FontAwesomeIcon icon={faBuildingColumns} className="ml-2 mr-1" /> Add BOE rate?</span>
                                <input type="checkbox" className="toggle toggle-primary mt-1" onChange={handleAddBOEToggleChange} checked={addBOE} />
                            </label>
                        </kbd>
                        <div className="tooltip tooltip-top tooltip-primary font-bold" data-tip={boeRateText}>
                            <button className="btn btn-primary font-bold btn-circle"><FontAwesomeIcon icon={faQuestion} /></button>
                        </div>
                    </div>
                    {/* Deposit input*/}
                    <div className={"flex items-center " + spaceFromTooltip}>
                        <label className="relative input-group">
                            <span className={inputElement + ' font-medium'}>£</span>
                            <span style={{ left: '3.25rem' }} className={`bg-transparent text-l font-normal text-gray-400 absolute px-2 transition-transform ease-in-out duration-200 z-10 ${depositValue ? 'transform -translate-y-1 -translate-x-3 scale-75' : 'translate-y-1/2'}`}>
                                Deposit
                            </span>
                            <input type="number" onChange={handleDepositInputChange} value={depositValue} className="input input-bordered w-full" />
                        </label>
                        <div className="tooltip tooltip-top tooltip-primary font-bold" data-tip={depositText}>
                            <button className="btn btn-primary font-bold btn-circle"><FontAwesomeIcon icon={faQuestion} /></button>
                        </div>
                    </div>
                    {/* Product Fee input*/}
                    <div className={"flex items-center " + spaceFromTooltip}>
                        <label className="relative input-group">
                            <span className={inputElement + ' font-medium'}>£</span>
                            <span style={{ left: '3.25rem' }} className={`bg-transparent text-l font-normal text-gray-400 absolute px-2 transition-transform ease-in-out duration-200 z-10 ${productFeeValue ? 'transform -translate-y-1 -translate-x-4 scale-75' : 'translate-y-1/2'}`}>
                                Product Fee
                            </span>
                            <input type="number" onChange={handleProductFeeInputChange} value={productFeeValue} className="input input-bordered w-full" />
                        </label>
                        <div className="tooltip tooltip-top tooltip-primary font-bold" data-tip={productFeeText}>
                            <button className="btn btn-primary font-bold btn-circle"><FontAwesomeIcon icon={faQuestion} /></button>
                        </div>
                    </div>
                </div>
            </div>
            {/*-------------------------- Advanced --------------------------------------*/}
            <div className={"collapse card bg-base-300 " + showAdvancedCollapse + " " + backgroundColour} style={{ overflow: 'visible', position: 'relative' }}>
                <div className="collapse-title p-3">
                    <div className="flex">
                        <div className="mr-2">
                            <h2 className="card-title font-bold"><FontAwesomeIcon icon={faGears} className="ml-2 mr-1" />Advanced</h2>
                        </div>
                        <div className="ml-auto pt-1">
                            <input type="checkbox" className="toggle" checked={showAdvanced} onChange={handleAdvancedToggleChange} />
                        </div>
                    </div>
                </div>
                <div className="collapse-content space-y-2">
                      {/* Date input*/}
                      <div className={"flex items-center " + spaceFromTooltip}>
                        <label className="relative w-full">
                            <span style={{ left: '3.25rem' }} className={`bg-transparent text-l font-normal text-gray-400 absolute  transition-transform ease-in-out duration-200 z-10 ${true ? 'transform -translate-y-1 -translate-x-11 scale-75' : 'translate-y-1/2'}`}>
                                Start Date
                            </span>
                            <input type="date" onChange={handleDateInputChange} value={dateValue} className="input input-bordered w-full" />
                        </label>
                        <div className="tooltip tooltip-top tooltip-primary font-bold" data-tip={dateText}>
                            <button className="btn btn-primary font-bold btn-circle"><FontAwesomeIcon icon={faQuestion} /></button>
                        </div>
                    </div>
                    {/* interest frequency input */}
                    <div className={"flex items-center " + spaceFromTooltip}>
                        <select value={interestFrequency} onChange={handleInterestFrequencyChange} className="select select-bordered w-full">
                            <option disabled selected value={""}>Interest Frequency</option>
                            {interestFrequencyOptions.map((option) => (
                                mortgageType === "interestOnly" && option.value === "daily" ?
                                    <option value={option.value} disabled>{option.label} (Not available for Interest-Only)</option> :
                                    <option value={option.value}>{option.label}</option>
                            ))}
                        </select>
                        <div className="tooltip tooltip-top tooltip-primary font-bold" data-tip={interestFrequencyText}>
                            <button className="btn btn-primary font-bold btn-circle"><FontAwesomeIcon icon={faQuestion} /></button>
                        </div>
                    </div>
                    {/* use nominal interest for repayment */}
                    <div className={"flex items-center " + spaceFromTooltip}>
                        <kbd className="kbd w-full">
                            <label className="cursor-pointer label w-6/12 justify-center flex items-center">
                                <span className="label-text font-sans font-semibold whitespace-nowrap pr-2 ">Effective interest for repayment calculation?</span>
                                <input type="checkbox" className="toggle toggle-primary mt-1" onChange={handleNominalToggleChange} checked={useNominal} />
                            </label>
                        </kbd>
                        <div className="tooltip tooltip-top tooltip-primary font-bold" data-tip={nominalText}>
                            <button className="btn btn-primary font-bold btn-circle"><FontAwesomeIcon icon={faQuestion} /></button>
                        </div>
                    </div>
                    {/* repayment frequency input */}
                    <div className={"flex items-center " + spaceFromTooltip}>
                        <select value={repaymentFrequency} onChange={handleRepaymentFrequencyChange} className="select select-bordered w-full">
                            <option disabled selected value={""}>Repayment Frequency</option>
                            {repaymentFrequencyOptions.map((option) => (
                                <option value={option.value}>{option.label}</option>
                            ))}
                        </select>
                        <div className="tooltip tooltip-top tooltip-primary font-bold" data-tip={repaymentFrequnecyText}>
                            <button className="btn btn-primary font-bold btn-circle"><FontAwesomeIcon icon={faQuestion} /></button>
                        </div>
                    </div>
                </div>
            </div>
            {/*-------------------------- Offset --------------------------------------*/}
            <div className={"collapse card bg-base-300 " + showOffsetCollapse + " " + backgroundColour} style={{ overflow: 'visible', position: 'relative' }}>
                <div className="collapse-title p-3">
                    <div className="flex">
                        <div className="mr-2">
                            <h2 className="card-title font-bold"><FontAwesomeIcon icon={faPiggyBank} className="ml-2 mr-1" />Offset</h2>
                            {mortgageType === "interestOnly" ? <p className="font-medium">(Not available for Interest-Only)</p> : ""}
                        </div>
                        <div className="ml-auto pt-1">
                            <input type="checkbox" className="toggle" checked={showOffset} onChange={handleOffsetToggleChange} disabled={mortgageType === "interestOnly"} />
                        </div>
                    </div>
                </div>
                <div className="collapse-content space-y-2">
                    {/* offset amount input */}
                    <div className={"flex items-center " + spaceFromTooltip}>
                        <label className="relative input-group">
                            <span className={inputElement + ' font-medium'}>£</span>
                            <span style={{ left: '3.25rem' }} className={`bg-transparent text-l font-normal text-gray-400 absolute px-2 transition-transform ease-in-out duration-200 z-10 ${offsetValue ? 'transform -translate-y-1 -translate-x-4 scale-75' : 'translate-y-1/2'}`}>
                                Starting Offset
                            </span>
                            <input type="number" onChange={handleOffsetInputChange} value={offsetValue} className="input input-bordered w-full" />
                        </label>
                        <div className="tooltip tooltip-top tooltip-primary font-bold" data-tip={offsetAmountText}>
                            <button className="btn btn-primary font-bold btn-circle"><FontAwesomeIcon icon={faQuestion} /></button>
                        </div>
                    </div>
                    {/* offset regular amount input */}
                    <div className={"flex items-center " + spaceFromTooltip}>
                        <label className="relative input-group">
                            <span className={inputElement + ' font-medium'}>£</span>
                            <span style={{ left: '3.25rem' }} className={`bg-transparent text-l font-normal text-gray-400 absolute px-2 transition-transform ease-in-out duration-200 z-10 ${offsetRegularValue ? 'transform -translate-y-1 -translate-x-4 scale-75' : 'translate-y-1/2'}`}>
                                Regular savings
                            </span>
                            <input type="number" onChange={handleOffsetRegularInputChange} value={offsetRegularValue} className="input input-bordered w-full" />
                        </label>
                        <div className="tooltip tooltip-top tooltip-primary font-bold" data-tip={offsetRegularText}>
                            <button className="btn btn-primary font-bold btn-circle"><FontAwesomeIcon icon={faQuestion} /></button>
                        </div>
                    </div>
                </div>
            </div>

            {/*-------------------------- Overpayments --------------------------------------*/}
            <div className={"collapse card bg-base-300 " + showOverpaymentsCollapse + " " + backgroundColour} style={{ overflow: 'visible', position: 'relative' }}>
                <div className="collapse-title p-3">
                    <div className="flex">
                        <div className="mr-2">
                            <h2 className="card-title font-bold"><FontAwesomeIcon icon={faMoneyBillTrendUp} className="ml-2 mr-1" />Overpayments</h2>
                            {mortgageType === "interestOnly" ? <p className="font-medium">(Not available for Interest-Only)</p> : ""}
                        </div>
                        <div className="ml-auto pt-1">
                            <input type="checkbox" className="toggle" checked={showOverpayments} onChange={handleOverpaymentsToggleChange} disabled={mortgageType === "interestOnly"} />
                        </div>
                    </div>
                </div>
                <div className="collapse-content space-y-2">
                    {/* overpayments input */}
                    <div className={"flex items-center " + spaceFromTooltip}>
                        <label className="relative input-group">
                            <span className={inputElement + ' font-medium'}>£</span>
                            <span style={{ left: '3.5rem' }} className={`bg-transparent text-l font-normal text-gray-400 absolute px-2 transition-transform ease-in-out duration-200 z-10 ${overpaymentsValue ? 'transform -translate-y-1 -translate-x-5 scale-75' : 'translate-y-1/2'}`}>
                                Regular overpayment
                            </span>
                            <input type="number" onChange={handleOverpaymentsInputChange} value={overpaymentsValue} className="input input-bordered w-full" />
                        </label>
                        <div className="tooltip tooltip-top tooltip-primary font-bold" data-tip={overpaymentsText}>
                            <button className="btn btn-primary font-bold btn-circle"><FontAwesomeIcon icon={faQuestion} /></button>
                        </div>
                    </div>
                </div>
            </div>
            {/*-------------------------- Equity --------------------------------------*/}
            {!isCompareVisible ? (<div className={"collapse card bg-base-300 " + showHousePriceCollapse + " " + backgroundColour} style={{ overflow: 'visible', position: 'relative' }}>
                <div className="collapse-title p-3">
                    <div className="flex">
                        <div className="mr-2">
                            <h2 className="card-title font-bold"><FontAwesomeIcon icon={faHouseUser} className="ml-2 mr-1" />Equity and LTV</h2>
                        </div>
                        <div className="ml-auto pt-1">
                            <input type="checkbox" className="toggle" checked={showEquity} onChange={handleHousePriceToggleChange} />
                        </div>
                    </div>
                </div>
                <div className="collapse-content space-y-2">
                    {/* house price apprication input */}
                    <div className={"flex items-center " + spaceFromTooltip}>
                        <label className="relative input-group">
                            <span className={inputElement + ' font-medium'}>%</span>
                            <span style={{ left: '3.5rem' }} className={`bg-transparent text-l font-normal text-gray-400 absolute px-2 transition-transform ease-in-out duration-200 z-10 ${housePriceInterestValue ? 'transform -translate-y-1 -translate-x-5 scale-75' : 'translate-y-1/2'}`}>
                                House appreciation
                            </span>
                            <input type="number" onChange={handleHousePriceInterestInputChange} value={housePriceInterestValue} className="input input-bordered w-full" />
                        </label>
                        <div className="tooltip tooltip-top tooltip-primary font-bold" data-tip={housePriceAppreciationText}>
                            <button className="btn btn-primary font-bold btn-circle"><FontAwesomeIcon icon={faQuestion} /></button>
                        </div>
                    </div>
                </div>
            </div>) : (<></>)}
        </div>
    );
});

export default InputForm;